import {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  isAxiosError
} from 'axios';
import { setAuthorizationHeaders } from '@/api/utils';
import AuthService from '@/services/authService.ts';
import { obtainPersonalAccessToken } from '@/api/deepsign/personalAccessApi.ts';
import { useDocumentAuth } from '@/composables/useDocumentAuth.ts';
import { useStorage } from '@vueuse/core';
import i18n from '@/plugins/i18n';

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

const interceptedClients = new WeakSet<AxiosInstance>();

export function applyDeepSignInterceptors(instance: AxiosInstance) {
  if (interceptedClients.has(instance)) return;

  instance.interceptors.request.use(async (config: RetryConfig) => {
    const locale = i18n.global.locale.value;
    const language = locale.includes('-') ? locale.split('-')[0] : locale;
    const headers: Record<string, string> = {
      'Accept-Language': language,
      'Content-Language': language
    };
    // A retry already carries the refreshed personal-access token.
    if (!config._retry) {
      await setAuthorizationHeaders(headers);
    }
    config.headers.set(headers);
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (!isAxiosError(error) || !error.config) {
        return Promise.reject(error);
      }
      const originalRequest: RetryConfig = error.config;
      const personalAccessConfig = useStorage(
        'personalAccessConfig',
        '',
        sessionStorage
      );
      const sessionPersonalAccessConfig = JSON.parse(
        personalAccessConfig.value || '{}'
      );
      const sid = new URLSearchParams(window.location.search).get('sid');

      if (
        !originalRequest._retry &&
        !AuthService.isAuthenticated &&
        sessionPersonalAccessConfig?.signKey &&
        sid &&
        sessionPersonalAccessConfig.sid === sid
      ) {
        const documentAuth = useDocumentAuth();
        originalRequest._retry = true;
        try {
          const tokenResponse = await obtainPersonalAccessToken(
            sessionPersonalAccessConfig.signKey,
            { headers: { Authorization: `SID ${sid}` } }
          );
          if (tokenResponse) {
            documentAuth.login(
              tokenResponse.data.documentId,
              tokenResponse.data.token
            );
            originalRequest.headers.set(
              'Authorization',
              `Bearer ${tokenResponse.data.token}`
            );
            return instance(originalRequest);
          }
        } catch (refreshError) {
          console.error('Error obtaining personal access token:', refreshError);
        }
      }
      return Promise.reject(error);
    }
  );
  interceptedClients.add(instance);
}
